/**
 * useAckController — Iter 36.5 Viewer Architecture Hardening.
 *
 * Owns the Field Acknowledgment Loop state for SheetViewer:
 *   - sheet-scope ack summary (drives the top-bar pill)
 *   - active review w/ impact_items (drives Inspector Revision Impact fusion)
 *   - caller's own ack (myAck — for the inline buttons)
 *   - deep-link routing (?review=rvw_… + ?focus=SMH-25)
 *   - auto-focus a structure when arriving with a deep-link
 *   - onAckResolved refresh trigger
 *   - focusFirstImpact click handler (top-bar pill → inspector)
 *
 * Pure extraction — semantics identical to the previous inline code in
 * SheetViewer.jsx. Sits between the network layer and the UI so future
 * "Object Intelligence Expansion" (timeline of acks per object) can plug
 * in without touching the viewer page.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";


export function useAckController({ sheetId, deepLinkReviewId, deepLinkFocus, inspector }) {
  const [ackSummary, setAckSummary] = useState(null);
  const [activeReview, setActiveReview] = useState(null);
  const [myAck, setMyAck] = useState(null);

  const loadAckContext = useCallback(async (sid, overrideReviewId) => {
    if (!sid) return;
    try {
      const { data: summary } = await api.get(`/sheets/${sid}/ack_summary`);
      setAckSummary(summary);
      const targetReviewId = overrideReviewId || summary?.latest_review?.id;
      if (targetReviewId) {
        const { data: rv } = await api.get(`/reviews/${targetReviewId}`);
        setActiveReview(rv);
        setMyAck(rv?.my_ack || null);
      } else {
        setActiveReview(null);
        setMyAck(null);
      }
    } catch (e) {
      // Non-essential — viewer keeps rendering even if ack endpoints fail.
      setActiveReview(null);
      setMyAck(null);
    }
  }, []);

  // Re-load ack context whenever the sheet changes OR an explicit
  // ?review=rvw_… deep-link is provided.
  useEffect(() => {
    loadAckContext(sheetId, deepLinkReviewId);
  }, [sheetId, deepLinkReviewId, loadAckContext]);

  // After ack mutation, refresh both summary AND active review so the
  // inspector + top-bar pill update in lockstep.
  const onAckResolved = useCallback((updated) => {
    setMyAck(updated);
    if (sheetId) loadAckContext(sheetId, deepLinkReviewId);
  }, [sheetId, deepLinkReviewId, loadAckContext]);

  // Top-bar ack pill click — opens the inspector on the active review's
  // first impact item, mimicking the deep-link flow.
  const focusFirstImpact = useCallback(async () => {
    if (!activeReview?.impact_items?.length || !sheetId) return;
    const first = activeReview.impact_items[0];
    try {
      const { data: structures } = await api.get(`/sheets/${sheetId}/structures`);
      const match = structures.find(
        (s) => (first.structure_id && first.structure_id === s.id)
            || s.structure_label === first.structure_label,
      );
      if (match) {
        inspector.open({ kind: "structure", id: match.id, payload: match });
      }
    } catch {/* non-essential */}
  }, [activeReview, sheetId, inspector]);

  // Auto-focus a structure when arriving with a deep-link.
  //   ?focus=SMH-25         — explicit structure label
  //   ?review=rvw_…         — implicit: use review's FIRST impact item
  const [autoFocusDone, setAutoFocusDone] = useState(false);
  useEffect(() => { setAutoFocusDone(false); }, [sheetId, deepLinkFocus, deepLinkReviewId]);
  useEffect(() => {
    if (autoFocusDone || !sheetId) return;
    const fallbackLabel = activeReview?.impact_items?.[0]?.structure_label;
    const targetLabel = deepLinkFocus || (deepLinkReviewId ? fallbackLabel : null);
    if (!targetLabel) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: structures } = await api.get(`/sheets/${sheetId}/structures`);
        if (cancelled) return;
        const match = structures.find((s) => s.structure_label === targetLabel);
        if (match) {
          inspector.open({ kind: "structure", id: match.id, payload: match });
          setAutoFocusDone(true);
        }
      } catch {/* non-essential */}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId, deepLinkFocus, deepLinkReviewId, activeReview?.id, autoFocusDone]);

  return {
    ackSummary,
    activeReview,
    myAck,
    onAckResolved,
    focusFirstImpact,
  };
}
